import type { BusinessHours } from './authManager';

export const BUSINESS_DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Format a Date as YYYY-MM-DD in local time (avoids UTC shift from toISOString). */
export function formatLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getDayNameFromDateStr(dateStr: string): string {
  const dateObj = new Date(`${dateStr}T00:00:00`);
  return BUSINESS_DAY_NAMES[dateObj.getDay()];
}

export function isBusinessOpenOnDate(
  businessHours: BusinessHours | undefined,
  dateStr: string,
): boolean {
  if (!businessHours) return true;
  const dayName = getDayNameFromDateStr(dateStr);
  const dayHours = businessHours[dayName];
  if (!dayHours) return false;
  return !!(dayHours.isOpen && dayHours.openTime && dayHours.closeTime);
}

/** Load business hours and booking policy from Firestore for booking flows. */
export async function fetchBusinessBookingSettings(businessId: string): Promise<{
  businessHours?: BusinessHours;
  appointmentDuration?: number;
  paymentRequiredAtBooking?: boolean;
}> {
  if (!businessId) return {};

  try {
    const { doc, getDoc } = require('firebase/firestore');
    const { getWebDb, initializeFirebase } = require('../services/unifiedAuthService');

    if (initializeFirebase()) {
      const firestoreDb = getWebDb();
      if (firestoreDb) {
        const businessDoc = await getDoc(doc(firestoreDb, 'businesses', businessId));
        if (businessDoc.exists()) {
          const data = businessDoc.data();
          return {
            businessHours: data.businessHours,
            appointmentDuration: data.appointmentDuration,
            paymentRequiredAtBooking: !!data.paymentRequiredAtBooking,
          };
        }
      }
    }
  } catch (error) {
    console.warn('fetchBusinessBookingSettings Firestore:', error);
  }

  try {
    const { WIDGET_BOOKING_API } = require('./widgetEmbed');
    const res = await fetch(`${WIDGET_BOOKING_API}/business/${encodeURIComponent(businessId)}`);
    if (res.ok) {
      const data = await res.json();
      return {
        businessHours: data.businessHours,
        appointmentDuration: data.appointmentDuration ?? undefined,
        paymentRequiredAtBooking: !!data.paymentRequiredAtBooking,
      };
    }
  } catch (error) {
    console.warn('fetchBusinessBookingSettings API:', error);
  }

  return {};
}

/**
 * Time options for business hours in 12-hour AM/PM format.
 * Values are stored as 24-hour "HH:mm" for compatibility with existing code.
 */
export const BUSINESS_HOURS_TIME_OPTIONS: { value: string; label: string }[] = (() => {
  const options: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
      options.push({ value, label });
    }
  }
  return options;
})();

/** Options grouped by AM/PM for dropdowns (no typing, tap to select). */
export const BUSINESS_HOURS_AM_OPTIONS: { value: string; label: string }[] = (() => {
  const options: { value: string; label: string }[] = [];
  for (let h = 0; h < 12; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hour12 = h === 0 ? 12 : h;
      const label = `${hour12}:${String(m).padStart(2, '0')} AM`;
      options.push({ value, label });
    }
  }
  return options;
})();

export const BUSINESS_HOURS_PM_OPTIONS: { value: string; label: string }[] = (() => {
  const options: { value: string; label: string }[] = [];
  for (let h = 12; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hour12 = h === 12 ? 12 : h - 12;
      const label = `${hour12}:${String(m).padStart(2, '0')} PM`;
      options.push({ value, label });
    }
  }
  return options;
})();

/** Format 24h "HH:mm" to 12h display (e.g. "09:00" -> "9:00 AM"). */
export function formatTime24To12(time24: string): string {
  if (!time24 || !/^\d{1,2}:\d{2}$/.test(time24)) return time24 || '—';
  const [hStr, mStr] = time24.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}
